// Simple API client for localhost:8000 protected by Clerk JWT
// Usage inside a component:
// const { getToken } = useAuth();
// const items = await getData(getToken);

import { isClerkEnabled } from '../utils/clerkConfig';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
const JWT_TEMPLATE = process.env.REACT_APP_CLERK_JWT_TEMPLATE || undefined;

async function authorizedFetch(getToken, path, options = {}) {
  let token;
  if (isClerkEnabled && typeof getToken === 'function') {
    // Add a 1.5-second timeout safeguard to prevent hanging if Clerk fails to load or resolves infinitely
    const tokenPromise = getToken(JWT_TEMPLATE ? { template: JWT_TEMPLATE } : undefined);
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 1500)
    );
    try {
      token = await Promise.race([tokenPromise, timeoutPromise]);
    } catch (e) {
      console.warn("Authentication token retrieval failed or timed out:", e);
      token = null;
    }
  } else if (isClerkEnabled && typeof window !== 'undefined' && window.Clerk && window.Clerk.session) {
    token = await window.Clerk.session.getToken(JWT_TEMPLATE ? { template: JWT_TEMPLATE } : undefined);
  }
  // If auth is required (Clerk enabled) but token is not ready, avoid making the request
  if (isClerkEnabled && !token) {
    throw new Error('AUTH_NOT_READY: Authentication not loaded or user not signed in.');
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
}

export async function getData(getToken) {
  return authorizedFetch(getToken, '/data', { method: 'GET' });
}

export async function postData(getToken, item) {
  return authorizedFetch(getToken, '/data', {
    method: 'POST',
    body: JSON.stringify(item || {}),
  });
}

export async function deleteData(getToken, id) {
  const qs = new URLSearchParams({ id }).toString();
  return authorizedFetch(getToken, `/data?${qs}`, { method: 'DELETE' });
}

export async function getExerciseDetails(getToken, exerciseId, lang) {
  try {
    return await authorizedFetch(getToken, `/exercises/${exerciseId}?lang=${lang}`, { method: 'GET' });
  } catch (e) {
    if (e && e.message && e.message.startsWith('AUTH_NOT_READY')) {
      const res = await fetch(`${API_BASE}/exercises/${exerciseId}?lang=${lang}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }
      return res.json();
    }
    throw e;
  }
}

export async function getExercisesList(lang) {
  const res = await fetch(`${API_BASE}/exercises?lang=${lang}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
}

